import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { WebSessionBootstrap } from "@/components/WebSessionBootstrap";
import { RestartNotificationProvider } from "@/hooks/useRestartNotifications";
import { APIKeysPage } from "@/pages/APIKeysPage";
import { AdminPage } from "@/pages/AdminPage";
import AppVersionDetailPage from "@/pages/AppVersionDetailPage";
import AppVersionFormPage from "@/pages/AppVersionFormPage";
import { AppVersionManagementPage } from "@/pages/AppVersionManagementPage";
import { AvailableModelsPage } from "@/pages/AvailableModelsPage";
import { ChannelManagementPage } from "@/pages/ChannelManagementPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { InvitationPage } from "@/pages/InvitationPage";
import { InvitationPageManagementPage } from "@/pages/InvitationPageManagementPage";
import { LoginPage } from "@/pages/LoginPage";
import { MallPage } from "@/pages/MallPage";
import { MyMCPConfigPage } from "@/pages/MyMCPConfigPage";
import OrderManagementPage from "@/pages/OrderManagementPage";
import { ProductDetailPage } from "@/pages/ProductDetailPage";
import { ProductManagementPage } from "@/pages/ProductManagementPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { RedemptionCodeManagementPage } from "@/pages/RedemptionCodeManagementPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { TopupPage } from "@/pages/TopupPage";
import { UserLogsPage } from "@/pages/UserLogsPage";
import { UserManagementPage } from "@/pages/UserManagementPage";
import { VIPActivationPage } from "@/pages/VIPActivationPage";
import { VIPExamplePage } from "@/pages/VIPExamplePage";
import VipCodeManagementPage from "@/pages/VipCodeManagementPage";
import { VIPProvider } from "@/providers/VIPProvider";
import { WebSocketProvider } from "@/providers/WebSocketProvider";
import { StatsPage } from "@/pages/StatsPage";
import { AdminDashboardPage } from "@/pages/AdminDashboardPage";
import { AdminAnalyticsPage } from "@/pages/AdminAnalyticsPage";
import { AccountActivationPage } from "@/pages/AccountActivationPage";
import { AccountActivationStatsPage } from "@/pages/AccountActivationStatsPage";
import { SkillManagementPage } from "@/pages/SkillManagementPage";
import { StickerManagementPage } from "@/pages/StickerManagementPage";
import { Navigate, Route, Routes } from "react-router-dom";

function App() {
  return (
    <WebSocketProvider>
      <VIPProvider>
        {/* App WebView 单点登录引导：在任意页面挂载时验证 web_session cookie */}
        <WebSessionBootstrap />

        {/* 重启通知管理器 - 全局监听重启状态变化 */}
        <RestartNotificationProvider />

        {/* 所有页面使用侧边栏布局，不再需要全局 Navbar */}

        <Routes>
          {/* 公开路由 */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/mall" element={<MallPage />} />
          <Route
            path="/mall/product/:productId"
            element={<ProductDetailPage />}
          />

          {/* 受保护路由 - 需要登录 */}
          <Route
            path="/my-mcp-config"
            element={
              <ProtectedRoute>
                <MyMCPConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/api-keys"
            element={
              <ProtectedRoute>
                <APIKeysPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/topup"
            element={
              <ProtectedRoute>
                <TopupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vip/activate"
            element={
              <ProtectedRoute>
                <VIPActivationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/available-models"
            element={
              <ProtectedRoute>
                <AvailableModelsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vip/example"
            element={
              <ProtectedRoute>
                <VIPExamplePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invitation"
            element={
              <ProtectedRoute>
                <InvitationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-logs"
            element={
              <ProtectedRoute>
                <UserLogsPage />
              </ProtectedRoute>
            }
          />

          {/* 管理员路由 - 需要管理员权限 */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminPage />
              </ProtectedRoute>
            }
          />

          {/* 管理员功能路由 */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredRole="admin">
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute requiredRole="admin">
                <ProductManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <ProtectedRoute requiredRole="admin">
                <OrderManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vip-codes"
            element={
              <ProtectedRoute requiredRole="admin">
                <VipCodeManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/redemption-codes"
            element={
              <ProtectedRoute requiredRole="admin">
                <RedemptionCodeManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/account-activations"
            element={
              <ProtectedRoute requiredRole="admin">
                <AccountActivationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/account-activations/stats"
            element={
              <ProtectedRoute requiredRole="admin">
                <AccountActivationStatsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/channels"
            element={
              <ProtectedRoute requiredRole="admin">
                <ChannelManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/skills"
            element={
              <ProtectedRoute requiredRole="admin">
                <SkillManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stickers"
            element={
              <ProtectedRoute requiredRole="admin">
                <StickerManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app-version"
            element={
              <ProtectedRoute requiredRole="admin">
                <AppVersionManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app-version/create"
            element={
              <ProtectedRoute requiredRole="admin">
                <AppVersionFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app-version/edit/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <AppVersionFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app-version/detail/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <AppVersionDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/invitation-page"
            element={
              <ProtectedRoute requiredRole="admin">
                <InvitationPageManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/stats"
            element={
              <ProtectedRoute requiredRole="admin">
                <StatsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminAnalyticsPage />
              </ProtectedRoute>
            }
          />

          {/* 默认重定向到商城 */}
          <Route path="/" element={<Navigate to="/mall" />} />
        </Routes>

        {/* Toast 通知容器 */}
        <Toaster
          richColors
          toastOptions={{
            classNames: {
              description: "group-[.toast]:text-muted-foreground",
              actionButton:
                "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
              cancelButton: "group-[.toast]:bg-white group-[.toast]:text-black",
              error:
                "group toast group-[.toaster]:bg-red group-[.toaster]:text-red-600 dark:group-[.toaster]:text-foreground group-[.toaster]:shadow-lg",
              success:
                "group toast group-[.toaster]:bg-green group-[.toaster]:text-green-600 dark:group-[.toaster]:text-foreground group-[.toaster]:shadow-lg",
              warning:
                "group toast group-[.toaster]:bg-yellow group-[.toaster]:text-yellow-600 dark:group-[.toaster]:text-foreground group-[.toaster]:shadow-lg",
              info: "group toast group-[.toaster]:bg-blue group-[.toaster]:text-blue-600 dark:group-[.toaster]:text-foreground group-[.toaster]:shadow-lg",
            },
          }}
        />
      </VIPProvider>
    </WebSocketProvider>
  );
}

export default App;
